import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';
import { FlatList, TouchableOpacity, View } from 'react-native';
import { addDays, format, isAfter, isBefore, isSameDay } from 'date-fns';

import { Octicons } from '@expo/vector-icons';
import { useUserContext } from '../context/UserContext';
import {
  UserAttributeType,
  UserAttribute,
  UserAttributeInputType,
  UserAttributeSource,
  OuraSpecificDataType
} from '../models/UserAttribute';
import Spacer from './Spacer';
import { H4, H5, B } from './Typography';
import EditMeasurementActionSheet, {
  WearableMeasurement
} from './modals/EditMeasurementActionSheet';
import ActivityIndicatorScreen from './ActivityIndicatorScreen';
import Colors from '../constants/Colors';
import { useFindUserAttributes } from '../hooks/user/useFindUserAttributes';
import AlertBox from './AlertBox';
import { getHoursLabel } from './charts/LineChart';
import { UserUnitSystem } from '../models/User';
import { convertToImperial } from './MyDataList';
import { displayedWearableConnection } from '../tools/WearableConnection';

const StyledContainer = styled.View`
  flex-shrink: 1;
  height: 100%;
  width: 100%;
`;

const StyleMeasurementsContainer = styled.View`
  width: 100%;
  background-color: white;
  border-radius: 16px;
  padding: 8px 8px 0 8px;
`;

const StyledRowContainer = styled.View`
  padding: 0 8px 0 8px;
  height: 64px;
  align-items: center;
  flex-direction: row;
  border-bottom-width: 0.2px;
  border-bottom-color: ${Colors.placeholderColor};
`;

const StyledItemView = styled.View`
  flex: 1;
`;

function formatDateString(date: any): string {
  const formattedDate = format(new Date(date), 'MMM d');
  return formattedDate;
}

type EditMeasurementsProps = {
  type: UserAttributeType | OuraSpecificDataType;
  config: {
    source: UserAttributeSource;
    subSource?: string;
    sourceName?: string;
  };
  disableEditing?: boolean;
  unitSystem?: UserUnitSystem;
  onSaved?: () => void;
};

const units = (unitSystem?: UserUnitSystem) => ({
  Sleep: 'h',
  'Blood Glucose': 'mmol/L',
  Steps: 'steps',
  'Body Temperature': unitSystem === UserUnitSystem.Imperial ? '°F' : '°C',
  'Calories Burned': 'cal',
  'Calories Consumed': 'cal',
  'Body Mass Index': '',
  'Body Fat Percentage': '%',
  'Blood Pressure': 'mmHg',
  Weight: unitSystem === UserUnitSystem.Imperial ? 'lb' : 'kg',
  'Active Energy Burned': 'cal',
  'Basal Energy Burned': 'cal',
  'Blood Alcohol Content': '%',
  Carbohydrates: 'g',
  'Energy Consumed': 'cal',
  Height: unitSystem === UserUnitSystem.Imperial ? 'ft' : 'cm',
  'Lean Body Mass': unitSystem === UserUnitSystem.Imperial ? 'lb' : 'kg',
  'Protein Consumed': 'g',
  'Total Fat Consumed': 'g',
  'Heart Rate Variability': 'ms',
  'Heart Rate': 'bpm',
  'Oxygen Saturation': '%',
  'Resting Heart Rate': 'bpm',
  // Oura additional attributes
  'Respiratory Rate': 'breaths/min',
  'Lowest Heart Rate': 'bpm',
  'Sleep Efficiency': '%',
  'Awake Time': 'min',
  'Deep Sleep Duration': 'min',
  'Sleep Latency': 'min',
  'Light Sleep Duration': 'min',
  'REM Sleep Duration': 'min',
  'Temperature Deviation': unitSystem === UserUnitSystem.Imperial ? '°F' : '°C',
  'Temperature Trend Deviation':
    unitSystem === UserUnitSystem.Imperial ? '°F' : '°C',
  'Active Calories': 'cal',
  'High Activity Time': 'min',
  'Low Activity Time': 'min',
  'Non-wear Time': 'min',
  'Sedentary Time': 'min',
  'SPO2 Nightly Disturbances': '',
  Workout: 'cal'
});

const EditMeasurements: React.FC<EditMeasurementsProps> = ({
  type,
  config,
  disableEditing,
  unitSystem = UserUnitSystem.Metric,
  onSaved
}) => {
  const { user } = useUserContext();
  const displayedUserId =
    displayedWearableConnection(user)?.userId ?? user?.id;
  const { source, sourceName } = config;
  const [selected, setSelected] = useState<undefined | WearableMeasurement>();
  const { loadingUserAttributes, findUserAttributes, userAttributesError } =
    useFindUserAttributes();
  const [userAttributeList, setUserAttributeList] = useState<UserAttribute[]>();
  const unit = useMemo(() => units(unitSystem)[type], [type, unitSystem]);

  const { manualUserAttributeList, wearableUserAttributeList } = useMemo(() => {
    const manualUserAttributeList: UserAttribute[] =
      userAttributeList?.filter(
        ua => ua.inputType === UserAttributeInputType.Manual
      ) || [];

    const wearableUserAttributeList: UserAttribute[] =
      userAttributeList?.filter(
        ua => ua.inputType === UserAttributeInputType.Wearable
      ) || [];

    return {
      manualUserAttributeList,
      wearableUserAttributeList
    };
  }, [userAttributeList]);

  const getTotalSleepValue = useCallback(
    (results: Array<any>, intendedStartDate: Date) => {
      const dataCategory =
        source === UserAttributeSource.HealthKit
          ? ['CORE', 'REM', 'DEEP', 'ASLEEP', 'UNKNOWN']
          : undefined;

      const minStartDate = addDays(intendedStartDate, -1);
      minStartDate.setHours(17, 59, 59, 59);
      const maxStartDate = addDays(minStartDate, 1);
      const maxEndDate = addDays(minStartDate, 2);
      maxEndDate.setHours(0, 0, 0, 0);
      const newResults = results.filter(result => {
        const endDate = new Date(result.endDate);
        const startDate = new Date(result.startDate);
        return (
          result.inputType === UserAttributeInputType.Wearable &&
          (dataCategory
            ? dataCategory.includes(result.jsonValue.value)
            : true) &&
          isAfter(startDate, minStartDate) &&
          isBefore(startDate, maxStartDate) &&
          isBefore(endDate, maxEndDate)
        );
      });
      const uniqueResults: Array<UserAttribute> = [];
      newResults.forEach(data => {
        if (
          !uniqueResults.some(
            uniqueData =>
              uniqueData.startDate == data.startDate &&
              uniqueData.endDate == data.endDate
          )
        ) {
          uniqueResults.push(data);
        }
      });

      let userAttributes: UserAttribute | undefined = undefined;
      if (uniqueResults.length > 0) {
        userAttributes = {
          startDate: uniqueResults[uniqueResults.length - 1].startDate,
          endDate: uniqueResults[0].endDate,
          numberValue: uniqueResults.reduce((prev, current) => {
            let currentVal = current?.numberValue || 0;
            if (current?.jsonValue?.minutesAsleep) {
              // Fix for fitbit sleep value including awake time
              currentVal = Number(
                (current.jsonValue.minutesAsleep / 60).toFixed(5)
              );
            }
            return prev + currentVal;
          }, 0)
        } as UserAttribute;
      }
      return userAttributes;
    },
    [source]
  );

  const listOfDates = useMemo(() => {
    const dates: Array<{ date: Date; userAttribute?: UserAttribute }> = [];
    for (let i = 0; i < 28; i++) {
      let userAttribute = manualUserAttributeList?.find(ua =>
        isSameDay(addDays(new Date(), -i), ua.endDate)
      );
      if (!userAttribute) {
        if (type === UserAttributeType.Sleep) {
          userAttribute = getTotalSleepValue(
            wearableUserAttributeList,
            addDays(new Date(), -i)
          );
        } else if (type === OuraSpecificDataType.Workout) {
          const totalCalories = wearableUserAttributeList
            ?.filter(ua => isSameDay(addDays(new Date(), -i), ua.endDate))
            ?.reduce((a, v) => a + (v?.numberValue || 0), 0);
          userAttribute = wearableUserAttributeList?.find(ua =>
            isSameDay(addDays(new Date(), -i), ua.endDate)
          );
          if (userAttribute?.numberValue) {
            userAttribute.numberValue = totalCalories;
          }
        } else if (type === UserAttributeType.Steps) {
          const daySamples = wearableUserAttributeList?.filter(ua =>
            isSameDay(addDays(new Date(), -i), ua.endDate)
          );
          if (daySamples && daySamples.length > 0) {
            userAttribute = {
              ...daySamples[0],
              numberValue: daySamples.reduce(
                (total, sample) => total + (sample.numberValue || 0),
                0
              )
            };
          }
        } else {
          userAttribute = wearableUserAttributeList?.find(ua =>
            isSameDay(addDays(new Date(), -i), ua.endDate)
          );
        }
      }
      dates.push({ date: addDays(new Date(), -i), userAttribute });
    }
    return dates;
  }, [
    getTotalSleepValue,
    manualUserAttributeList,
    type,
    wearableUserAttributeList
  ]);

  const getUserAttributes = useCallback(async () => {
    if (!user?.id) return;
    const startDate = format(addDays(new Date(), -28), 'yyyy-MM-dd');
    const endDate = format(new Date(), 'yyyy-MM-dd');
    const result = await findUserAttributes(
      user.id,
      type,
      source,
      sourceName,
      undefined,
      startDate,
      endDate
    );
    setUserAttributeList(result);
  }, [source, sourceName, findUserAttributes, type, user?.id]);

  useEffect(() => {
    getUserAttributes();
  }, [getUserAttributes]);
  return (
    <StyledContainer>
      <H4 color='dark'>Edit Measurements</H4>
      <Spacer />
      <StyleMeasurementsContainer>
        <StyledRowContainer>
          <StyledItemView>
            <H5 color='dark'>Date</H5>
          </StyledItemView>
          <StyledItemView style={{ marginRight: 34 }}>
            <H5 color='dark'>{type}</H5>
          </StyledItemView>
        </StyledRowContainer>
        <FlatList
          scrollEnabled={false}
          data={listOfDates}
          keyExtractor={item => item.date.toString()}
          renderItem={({ item }) => {
            return (
              <TouchableOpacity
                disabled={disableEditing}
                onPress={() => {
                  setSelected(item);
                }}
              >
                <StyledRowContainer>
                  <StyledItemView>
                    <B color='#615E6D'>{`${formatDateString(item.date)}${
                      item.userAttribute?.inputType ===
                      UserAttributeInputType.Manual
                        ? ' *'
                        : ''
                    }`}</B>
                  </StyledItemView>
                  <StyledItemView style={{ paddingRight: 10 }}>
                    <B numberOfLines={1} ellipsizeMode='middle' color='dark'>
                      {item?.userAttribute
                        ? unit === units(unitSystem).Sleep
                          ? getHoursLabel(item.userAttribute.numberValue || 0)
                          : unit === units(unitSystem)['Blood Pressure']
                          ? `${item.userAttribute.textValue} ${unit}`
                          : `${
                              item.userAttribute.numberValue
                                ? (unitSystem === UserUnitSystem.Imperial
                                    ? convertToImperial(
                                        item.userAttribute.numberValue,
                                        unit
                                      )
                                    : item.userAttribute.numberValue
                                  )?.toLocaleString(
                                    undefined,
                                    unit === '°F' || unit === '°C'
                                      ? {
                                          maximumFractionDigits: 1,
                                          minimumFractionDigits: 0
                                        }
                                      : {}
                                  )
                                : 0
                            } ${unit}`
                        : ''}
                    </B>
                  </StyledItemView>
                  {!disableEditing && (
                    <View>
                      <Octicons name='pencil' color='#615E6D' size={24} />
                    </View>
                  )}
                </StyledRowContainer>
              </TouchableOpacity>
            );
          }}
        />
        {loadingUserAttributes && <ActivityIndicatorScreen modal />}
      </StyleMeasurementsContainer>
      {user && displayedUserId && selected && (
        <EditMeasurementActionSheet
          unitSystem={unitSystem}
          visible
          userId={displayedUserId}
          type={type}
          source={source}
          sourceName={sourceName}
          data={selected}
          unit={unit}
          onRequestClose={() => setSelected(undefined)}
          onSaved={() => {
            getUserAttributes();
            setSelected(undefined);
            onSaved?.();
          }}
        />
      )}
      {userAttributesError && (
        <AlertBox severity='error'>{userAttributesError.message}</AlertBox>
      )}
    </StyledContainer>
  );
};
export default EditMeasurements;
